import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis } from 'recharts'
import { Loader2, Save, Smartphone, X } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import type { StorefrontAppInstallDay } from '@/types'

/**
 * Admin → Website → App (F-FE-N195).
 *
 * The ordering site installs as an app on customers' phones (Android in one
 * tap, iPhone through Add to Home Screen). Here the tenant switches the
 * "Get the app" prompt on or off, sets an optional line for it, and sees how
 * many customers installed, how many use it, and how much of their online
 * ordering now comes from the app.
 */

const LINE_MAX = 80
const DEFAULT_LINE = 'Order in a tap, follow your order and stay signed in.'

interface StoredAppSetting {
  prompt_enabled?: boolean
  prompt_line?: string
}

export function AdminStorefrontApp() {
  return (
    <div className="space-y-5">
      <PromptSettings />
      <AppFigures />
    </div>
  )
}

function PromptSettings() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const settingsQuery = useQuery({ queryKey: ['settings', 'all'], queryFn: () => apiClient.getAllSettings() })
  const d = settingsQuery.data?.data as Record<string, unknown> | undefined
  const stored = (d?.storefront_app ?? null) as StoredAppSetting | null
  const restaurant = typeof d?.restaurant_name === 'string' && d.restaurant_name ? d.restaurant_name : 'your restaurant'

  const [enabled, setEnabled] = useState(true)
  const [line, setLine] = useState('')
  useEffect(() => {
    if (!settingsQuery.isSuccess) return
    setEnabled(stored?.prompt_enabled ?? true)
    setLine(stored?.prompt_line ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.isSuccess, settingsQuery.dataUpdatedAt])

  const save = useMutation({
    mutationFn: () => apiClient.updateSetting('storefront_app', { prompt_enabled: enabled, prompt_line: line.trim() }),
    onSuccess: () => {
      toast({ title: 'App settings saved' })
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
    },
    onError: (err) =>
      toast({ title: 'Could not save', description: err instanceof Error ? err.message : String(err), variant: 'destructive' }),
  })

  return (
    <Card className="space-y-5 p-5">
      <div className="flex items-center gap-3 border-b border-border/60 pb-3">
        <Smartphone className="h-5 w-5 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">“Get the app” on your website</h3>
          <p className="text-xs text-muted-foreground">
            On phones and tablets, customers see an App button in the top bar and a small bar that rises as they scroll. They
            can close the bar; it stays away for 14 days. It never shows during checkout or once the app is installed.
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} aria-label="Show Get the app on your website" />
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between">
            <label htmlFor="app-line" className="text-sm font-medium">
              Your line <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {line.length}/{LINE_MAX}
            </span>
          </div>
          <Input
            id="app-line"
            value={line}
            maxLength={LINE_MAX}
            disabled={!enabled}
            placeholder={DEFAULT_LINE}
            onChange={(e) => setLine(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Give people a reason, e.g. “10% off your first app order”. Leave empty for the standard line. If you promise an
            offer here, set up the promo code too.
          </p>
        </div>

        {/* What the customer sees */}
        <div className="space-y-1.5">
          <p className="text-sm font-medium">Preview</p>
          <div className={`rounded-lg border bg-muted/40 p-3 ${enabled ? '' : 'opacity-50'}`}>
            <div className="flex items-center gap-2.5 rounded-lg border bg-background p-2.5 shadow-sm">
              <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[22%] bg-primary text-sm font-semibold text-primary-foreground">
                {restaurant.charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold">Get our app</p>
                <p className="line-clamp-2 text-[11px] leading-snug text-muted-foreground">{line.trim() || DEFAULT_LINE}</p>
              </div>
              <span className="flex-none rounded-md bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground">Install</span>
              <X className="h-3.5 w-3.5 flex-none text-muted-foreground" aria-hidden />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end border-t border-border/60 pt-4">
        <Button type="button" onClick={() => save.mutate()} disabled={save.isPending || !settingsQuery.isSuccess} className="gap-1.5">
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </Button>
      </div>
    </Card>
  )
}

const nf = new Intl.NumberFormat('en-PK')

function dayLabel(day: string) {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function AppFigures() {
  const statsQuery = useQuery({
    queryKey: ['storefront-app', 'stats', 30],
    queryFn: () => apiClient.getStorefrontAppStats(30),
    staleTime: 60_000,
  })
  const s = statsQuery.data?.data
  const ordersTotal = (s?.orders_app ?? 0) + (s?.orders_web ?? 0)
  const share = ordersTotal > 0 ? Math.round(((s?.orders_app ?? 0) / ordersTotal) * 100) : null
  const total = s ? s.installs_total.android + s.installs_total.ios + s.installs_total.other : 0

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">How the app is doing · last 30 days</h3>
        {s?.tracking_since && <p className="text-xs text-muted-foreground">Counting since {dayLabel(s.tracking_since)}</p>}
      </div>

      {statsQuery.isError ? (
        <Card className="p-5 text-sm text-muted-foreground">Couldn’t load the app figures. Refresh to try again.</Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              loading={statsQuery.isLoading}
              label="Installs"
              value={s ? nf.format(s.installs_window) : ''}
              hint={s ? `${nf.format(total)} all time` : ''}
            />
            <Stat
              loading={statsQuery.isLoading}
              label="Customers using the app"
              value={s ? nf.format(s.active_devices) : ''}
              hint="Phones that opened it"
            />
            <Stat
              loading={statsQuery.isLoading}
              label="Orders from the app"
              value={share === null ? '—' : `${share}%`}
              hint={s ? (ordersTotal > 0 ? `${nf.format(s.orders_app)} of ${nf.format(ordersTotal)} website orders` : 'No website orders yet') : ''}
            />
            <Stat
              loading={statsQuery.isLoading}
              label="Order updates on"
              value={s ? nf.format(s.push_subscribers) : ''}
              hint="Phones getting notifications"
            />
          </div>
          <InstallsChart loading={statsQuery.isLoading} days={s?.installs_by_day ?? []} />
        </>
      )}

      <p className="text-xs text-muted-foreground">
        Android customers install in one tap. iPhones don’t allow that, so they get a short guide (Share → Add to Home
        Screen) and are counted the first time they open the app from their home screen. Customers who open your link inside
        Instagram or Facebook are asked to open it in their browser first.
      </p>
    </div>
  )
}

function Stat({ label, value, hint, loading }: { label: string; value: string; hint: string; loading: boolean }) {
  return (
    <Card className="p-4">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      {loading ? (
        <Skeleton className="mt-2 h-7 w-16" />
      ) : (
        <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">{value}</p>
      )}
      <p className="mt-0.5 truncate text-xs text-muted-foreground">{loading ? ' ' : hint}</p>
    </Card>
  )
}

const SERIES = [
  { key: 'android', label: 'Android', color: 'var(--chart-android)' },
  { key: 'ios', label: 'iPhone & iPad', color: 'var(--chart-ios)' },
] as const

function InstallsChart({ days, loading }: { days: StorefrontAppInstallDay[]; loading: boolean }) {
  const [asTable, setAsTable] = useState(false)
  const any = useMemo(() => days.some((d) => d.android + d.ios + d.other > 0), [days])
  // Tick every ~week so a 30-day axis never crowds.
  const ticks = useMemo(() => days.filter((_, i) => i % 7 === 0 || i === days.length - 1).map((d) => d.day), [days])

  return (
    <Card className="p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold">Installs per day</p>
          <div className="mt-1 flex items-center gap-4 text-xs text-muted-foreground">
            {SERIES.map((sr) => (
              <span key={sr.key} className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: sr.color }} aria-hidden />
                {sr.label}
              </span>
            ))}
          </div>
        </div>
        {any && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setAsTable((v) => !v)}>
            {asTable ? 'Show chart' : 'Show as table'}
          </Button>
        )}
      </div>

      {loading ? (
        <Skeleton className="h-52 w-full" />
      ) : !any ? (
        <div className="flex h-52 items-center justify-center text-center text-sm text-muted-foreground">
          No installs yet. They’ll show here as customers add the app.
        </div>
      ) : asTable ? (
        <div className="max-h-52 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card text-xs text-muted-foreground">
              <tr>
                <th className="py-1.5 text-left font-medium">Day</th>
                <th className="py-1.5 text-right font-medium">Android</th>
                <th className="py-1.5 text-right font-medium">iPhone & iPad</th>
              </tr>
            </thead>
            <tbody>
              {days
                .filter((d) => d.android + d.ios > 0)
                .reverse()
                .map((d) => (
                  <tr key={d.day} className="border-t border-border/60">
                    <td className="py-1.5">{dayLabel(d.day)}</td>
                    <td className="py-1.5 text-right tabular-nums">{d.android}</td>
                    <td className="py-1.5 text-right tabular-nums">{d.ios}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="h-52 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={days} margin={{ top: 8, right: 4, left: 0, bottom: 0 }} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis
                dataKey="day"
                ticks={ticks}
                tickFormatter={dayLabel}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                tickMargin={8}
              />
              <YAxis
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={32}
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              />
              <ReTooltip cursor={{ fill: 'hsl(var(--muted) / 0.5)' }} content={<DayTooltip />} />
              {/* Stacked, with a 2px surface gap between the two segments. */}
              <Bar dataKey="android" stackId="p" fill="var(--chart-android)" stroke="hsl(var(--card))" strokeWidth={2} maxBarSize={22} isAnimationActive={false} />
              <Bar dataKey="ios" stackId="p" fill="var(--chart-ios)" stroke="hsl(var(--card))" strokeWidth={2} radius={[4, 4, 0, 0]} maxBarSize={22} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  )
}

function DayTooltip({ active, payload }: { active?: boolean; payload?: { payload: StorefrontAppInstallDay }[] }) {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-md">
      <p className="mb-1 font-medium">{dayLabel(d.day)}</p>
      {SERIES.map((sr) => (
        <p key={sr.key} className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <span className="h-2 w-2 rounded-sm" style={{ background: sr.color }} aria-hidden />
            {sr.label}
          </span>
          <span className="tabular-nums">{d[sr.key]}</span>
        </p>
      ))}
    </div>
  )
}
